import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import clsx from 'clsx';
import styles from './MetricsKPISidebar.module.scss';
import { DisplayMetric, KPI_TREND_COLORS, METRIC_PLACEHOLDER, MetricTrend } from './constants';
import { useFinancialMetrics } from './useFinancialMetrics';
import { arrowTopRightWhite } from '../../../../../../assets/hostedassets';
import { TabModule } from '../../../../../../utils/constants/commonConstants';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';

const Sparkline = ({ metric }: { metric: DisplayMetric }) => {
    const color = KPI_TREND_COLORS[metric.trend ?? 'flat'];
    // Sanitise the key — raw metric names contain spaces/()/% which are invalid
    // in an SVG id and break the url(#…) gradient reference (fills go solid).
    const gradientId = `kpiSpark-${metric.key.replace(/[^a-zA-Z0-9]/g, '')}`;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={metric.sparkline} margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <YAxis hide domain={[0, 1]} />
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#${gradientId})`}
                    baseValue={0}
                    dot={false}
                    isAnimationActive={true}
                    animationDuration={900}
                    animationEasing="ease-out"
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

const deltaClass: Record<MetricTrend, string> = {
    up: 'KpiDeltaUp',
    down: 'KpiDeltaDown',
    flat: 'KpiDeltaFlat',
};

const DeltaPill = ({ metric }: { metric: DisplayMetric }) => {
    if (!metric.delta || !metric.trend) return null;
    return <span className={clsx(styles.KpiDelta, styles[deltaClass[metric.trend]])}>{metric.delta}</span>;
};

const miniDeltaClass: Record<MetricTrend, string> = {
    up: 'MiniDeltaUp',
    down: 'MiniDeltaDown',
    flat: 'MiniDeltaFlat',
};

const MiniDeltaPill = ({ metric }: { metric: DisplayMetric }) => {
    if (!metric.delta || !metric.trend) return null;
    return <span className={clsx(styles.MiniDelta, styles[miniDeltaClass[metric.trend]])}>{metric.delta}</span>;
};

function MetricsKPISidebar({
    orgId,
    navigateToTab,
    isLocked = false,
}: {
    orgId?: string;
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
    isLocked?: boolean;
}) {
    const { kpiMetrics, sparklineMetrics, fyLabel } = useFinancialMetrics(orgId);
    // Mobile: show the financial overview only — no redirect to the full financials page (not mobile-ready yet).
    const isMobile = useIsMobile();

    const handleViewFinancials = () => {
        if (isLocked) return;
        navigateToTab?.(TabModule.AUDITED_FINANCIALS);
    };

    // Hide the card when financial data is unavailable — i.e. no KPI metric has
    // a real value (every value is the empty placeholder).
    const hasFinancialData = kpiMetrics.some((metric) => metric.value !== METRIC_PLACEHOLDER);
    if (kpiMetrics.length === 0 || !hasFinancialData) return null;

    return (
        <div className={styles.MetricsCard}>
            <div className={styles.Header}>
                <div className={styles.HeaderTitle}>
                    <span className={styles.Heading}>Key Financial Metrics</span>
                    {fyLabel && <span className={styles.FyBadge}>({fyLabel})</span>}
                </div>
                {!isLocked && !isMobile && (
                    <div className={styles.FinancialsRight} onClick={handleViewFinancials}>
                        <span className={styles.FinancialsText}>View Financials</span>
                        <div className={styles.FinancialsArrow}>
                            <img src={arrowTopRightWhite} alt="" />
                        </div>
                    </div>
                )}
            </div>
            <div className={styles.Body}>
                {kpiMetrics.map((metric) => (
                    <div key={metric.key} className={styles.KpiCard}>
                        <div className={styles.KpiInfo}>
                            <span className={styles.KpiLabel}>{metric.label}</span>
                            <div className={styles.KpiValueRow}>
                                <span className={styles.KpiValue}>{metric.value}</span>
                                <DeltaPill metric={metric} />
                            </div>
                        </div>
                        <div className={styles.KpiChart}>
                            <Sparkline metric={metric} />
                        </div>
                    </div>
                ))}
                {sparklineMetrics.length > 0 && (
                    <div className={styles.MiniGrid}>
                        {sparklineMetrics.map((metric) => (
                            <div key={metric.key} className={styles.MiniCard}>
                                <span className={styles.MiniLabel}>{metric.label}</span>
                                <div className={styles.MiniValueRow}>
                                    <span className={styles.MiniValue}>{metric.value}</span>
                                    <MiniDeltaPill metric={metric} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MetricsKPISidebar;
